/**
 * Preparing an image for an MMS in the browser. The SERVER is the authority (it re-checks the real
 * bytes, the type and the size); this only gets a picture under the limit and shows the agent the
 * exact result that will be sent.
 */
export const MMS_IMAGE_TYPES = ["image/jpeg", "image/png"] as const;
export const MMS_MAX_TEXT_CHARS = 5000;
const MAX_EDGE = 1600;

export type ImageCheck =
  | { ok: true; reencode: boolean }
  | { ok: false; message: string };

/** Pure decision, unit-tested: may this file be attached, and must it be re-encoded to fit? */
export function checkImage(
  type: string,
  bytes: number,
  width: number,
  height: number,
  maxBytes: number,
): ImageCheck {
  if (!(MMS_IMAGE_TYPES as readonly string[]).includes(type)) {
    return {
      ok: false,
      message:
        "Only JPEG and PNG images can be sent. Save the picture as JPEG or PNG and try again.",
    };
  }
  if (bytes === 0) {return { ok: false, message: "The image is empty." };}
  return {
    ok: true,
    reencode: bytes > maxBytes || Math.max(width, height) > MAX_EDGE,
  };
}

export interface PreparedImage {
  blob: Blob;
  type: "image/jpeg" | "image/png";
  width: number;
  height: number;
  reencoded: boolean;
}

/** Re-encodes to JPEG, shrinking until it fits. Browser only (canvas). */
export async function prepareImageForMms(
  file: Blob,
  maxBytes: number,
): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(file);
  try {
    const check = checkImage(
      file.type,
      file.size,
      bitmap.width,
      bitmap.height,
      maxBytes,
    );
    if (!check.ok) {throw new Error(check.message);}
    if (!check.reencode) {
      return {
        blob: file,
        type: file.type as "image/jpeg" | "image/png",
        width: bitmap.width,
        height: bitmap.height,
        reencoded: false,
      };
    }
    let scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    for (let round = 0; round < 6; round++) {
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {throw new Error("This browser cannot prepare the image.");}
      ctx.fillStyle = "#ffffff"; // JPEG has no transparency
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(bitmap, 0, 0, w, h);
      for (const quality of [0.85, 0.75, 0.65]) {
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", quality),
        );
        if (blob && blob.size <= maxBytes) {
          return {
            blob,
            type: "image/jpeg",
            width: w,
            height: h,
            reencoded: true,
          };
        }
      }
      scale *= 0.8;
    }
    throw new Error(
      "The image could not be made small enough to send. Try a smaller picture.",
    );
  } finally {
    bitmap.close();
  }
}

export function formatBytes(n: number): string {
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1000))} KB`;
}
